import {
  Component,
  Element,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  h,
  Host,
  JSX,
  Prop,
  State,
  Watch,
} from '@stencil/core';
import { Matrix4 } from '@vertexvis/geometry';
import { Color, Disposable } from '@vertexvis/utils';

import { PinController } from '../../lib/pins/controller';
import { PinsInteractionHandler } from '../../lib/pins/interactions';
import {
  Pin,
  PinModel,
  ViewerPinToolMode,
  ViewerPinToolType,
} from '../../lib/pins/model';
import { getMarkupBoundingClientRect } from '../viewer-markup/dom';

interface StateMap {
  shouldClearDepthBuffers?: boolean;
  shouldClearFeatureMaps?: boolean;
  shouldClearCameraControls?: boolean;
  shouldClearKeyboardControls?: boolean;
}

@Component({
  tag: 'vertex-viewer-pin-tool',
  styleUrl: 'viewer-pin-tool.css',
  shadow: true,
})
export class ViewerPinTool {
  /**
   * The controller that is responsible for drawing pins and updating the model
   */
  @Prop({ mutable: true })
  public pinController?: PinController;

  /**
   * The model that contains the entities and outcomes from performing pin annotations
   */
  @Prop()
  public pinModel: PinModel = new PinModel();

  /**
   * The viewer that this component is bound to. This is automatically assigned
   * if added to the light-dom of a parent viewer element.
   */
  @Prop()
  public viewer?: HTMLVertexViewerElement;

  /**
   * The type of pin.
   *
   * This property will automatically be set.
   */
  @Prop({ mutable: true })
  public tool: ViewerPinToolType = 'pin-icon';

  /**
   * The mode of the pin tool
   */
  @Prop({ mutable: true })
  public mode: ViewerPinToolMode = 'view';

  /**
   * The primary color for new pins. Setting this will override the primary template color, and will be used for any new
   * pins created with this `vertex-viewer-pin-tool`. This styling applies to pin anchors, and borders, etc.
   */
  @Prop({ mutable: true })
  public primaryColor: Color.Color | string | undefined;

  /**
   * The accent color for new pins. Setting this will override the accent template color, and will be used for any new
   * pins created with this `vertex-viewer-pin-tool`. This styling applies to some background colors, etc
   */
  @Prop({ mutable: true })
  public accentColor: Color.Color | string | undefined;

  @Element()
  private hostEl!: HTMLElement;

  @State()
  private pins: Pin[] = [];

  @State()
  private selectedPinId?: string;

  @State()
  private elementBounds?: DOMRect;

  @State()
  private projectionViewMatrix?: Matrix4.Matrix4;

  private registeredInteractionHandler?: Promise<Disposable>;
  private onEntitiesChangedHandler?: Disposable;
  private onEntityAddedHandler?: Disposable;

  private resizeObserver?: ResizeObserver;

  private stateMap: StateMap = {};

  /**
   * @ignore
   */
  @Watch('mode')
  protected watchModeChange(): void {
    this.pinController?.setToolMode(this.mode);
    this.setupInteractionHandler();

    if (this.mode === 'edit') {
      this.setFeatureMaps();
      this.setCameraAndKeyboardControls();
    } else {
      this.resetFeatureMaps();
      this.resetCameraAndKeyboardControls();
    }
  }

  /**
   * @ignore
   */
  @Watch('tool')
  protected watchTypeChange(): void {
    this.pinController?.setToolType(this.tool);
    this.setupInteractionHandler();
  }

  /**
   * @ignore
   */
  @Watch('accentColor')
  protected watchAccentColorChange(): void {
    this.pinController?.setAccentColor(this.accentColor);
    this.setupInteractionHandler();
  }

  /**
   * @ignore
   */
  @Watch('primaryColor')
  protected watchPrimaryColorChange(): void {
    this.pinController?.setPrimaryColor(this.primaryColor);
    this.setupInteractionHandler();
  }

  /**
   * @ignore
   */
  @Watch('pins')
  protected watchPinsChange(): void {
    if (this.pins.length > 0) {
      this.setDepthBuffers();
      this.setFeatureMaps();
    } else {
      this.resetDepthBuffers();
      this.resetFeatureMaps();
    }
  }

  /**
   * @ignore
   */
  protected connectedCallback(): void {
    this.setupInteractionHandler();
    this.setDepthBuffers();
    this.setFeatureMaps();
    this.setCameraAndKeyboardControls();
  }

  /**
   * @ignore
   */
  protected componentWillLoad(): void {
    this.updateViewport();
    this.setupController();
    this.setupInteractionHandler();
    this.setDepthBuffers();
    this.setFeatureMaps();
    this.setCameraAndKeyboardControls();

    this.pinModel.onEntitiesChanged((entities) => {
      this.pins = entities;
    });

    this.pinModel.onSelectionChange((selectedId) => {
      this.selectedPinId = selectedId;
    });

    this.handleViewerChanged(this.viewer, undefined);
  }

  protected componentDidLoad(): void {
    this.resizeObserver = new ResizeObserver(() => this.updateViewport());
    this.resizeObserver.observe(this.hostEl);
  }

  /**
   * @ignore
   */
  protected disconnectedCallback(): void {
    this.clearInteractionHandler();
    this.clearModelListeners();
    this.resetDepthBuffers();
    this.resetFeatureMaps();
    this.resetCameraAndKeyboardControls();
  }

  /**
   * @ignore
   */
  @Watch('viewer')
  protected handleViewerChanged(
    newViewer?: HTMLVertexViewerElement,
    oldViewer?: HTMLVertexViewerElement
  ): void {
    this.setupInteractionHandler();
    this.setDepthBuffers();
    this.setFeatureMaps();
    this.setCameraAndKeyboardControls();

    if (oldViewer != null) {
      oldViewer.removeEventListener(
        'frameDrawn',
        this.handleSetProjectionMatrix
      );
    }

    if (newViewer != null) {
      newViewer.addEventListener('frameDrawn', this.handleSetProjectionMatrix);
    }

    this.handleSetProjectionMatrix();
  }

  private handleSetProjectionMatrix = (): void => {
    this.projectionViewMatrix =
      this.viewer?.frame?.scene.camera.projectionViewMatrix;
  };

  protected render(): JSX.Element {
    return (
      <Host>
        <vertex-viewer-dom-renderer viewer={this.viewer} drawMode="2d">
          {this.pins.map((pin) => {
            return (
              <vertex-viewer-pin-group
                id={`pin-group-${pin.id}`}
                data-is-dom-group-element={true}
                pin={pin}
                elementBounds={this.elementBounds}
                pinModel={this.pinModel}
                pinController={this.pinController}
                projectionViewMatrix={this.projectionViewMatrix}
                selected={this.selectedPinId === pin.id}
              ></vertex-viewer-pin-group>
            );
          })}
        </vertex-viewer-dom-renderer>
      </Host>
    );
  }

  private setupController(): void {
    const attributes =
      this.accentColor || this.primaryColor
        ? {
            accentColor: this.accentColor,
            primaryColor: this.primaryColor,
          }
        : undefined;
    this.pinController = new PinController(
      this.pinModel,
      this.mode,
      this.tool,
      attributes
    );
  }

  private clearInteractionHandler(): void {
    this.registeredInteractionHandler?.then((handler) => handler.dispose());
    this.registeredInteractionHandler = undefined;
  }

  private setupInteractionHandler(): void {
    const hostStyles = window.getComputedStyle(this.hostEl);

    const xOffset = hostStyles
      .getPropertyValue('--viewer-pin-tool-initial-label-offset-x')
      .trim();
    const xOffsetAsNumber = Number.isFinite(Number.parseInt(xOffset))
      ? Number.parseInt(xOffset)
      : 20;

    const yOffset = hostStyles
      .getPropertyValue('--viewer-pin-tool-initial-label-offset-y')
      .trim();
    const yOffsetAsNumber = Number.isFinite(Number.parseInt(yOffset))
      ? Number.parseInt(yOffset)
      : 20;

    this.clearInteractionHandler();

    if (this.pinController != null) {
      this.registeredInteractionHandler =
        this.viewer?.registerInteractionHandler(
          new PinsInteractionHandler(
            this.pinController,
            xOffsetAsNumber,
            yOffsetAsNumber
          )
        );
    }
  }

  private clearModelListeners(): void {
    this.onEntitiesChangedHandler?.dispose();
    this.onEntitiesChangedHandler = undefined;
    this.onEntityAddedHandler?.dispose();
    this.onEntityAddedHandler = undefined;
    this.resizeObserver?.disconnect();
  }

  private updateViewport(): void {
    this.elementBounds = getMarkupBoundingClientRect(this.hostEl);
  }

  private setDepthBuffers(): void {
    if (
      this.pins.length > 0 &&
      this.viewer != null &&
      this.viewer.depthBuffers == null
    ) {
      this.stateMap.shouldClearDepthBuffers = true;
      this.viewer.depthBuffers = 'final';
    }
  }

  private resetDepthBuffers(): void {
    if (this.stateMap.shouldClearDepthBuffers && this.viewer != null) {
      this.viewer.depthBuffers = undefined;
      this.stateMap.shouldClearDepthBuffers = undefined;
    }
  }

  private setFeatureMaps(): void {
    if (
      (this.pins.length > 0 || this.mode === 'edit') &&
      this.viewer != null &&
      this.viewer.featureMaps == null
    ) {
      this.stateMap.shouldClearFeatureMaps = true;
      this.viewer.featureMaps = 'final';
    }
  }

  private resetFeatureMaps(): void {
    if (this.stateMap.shouldClearFeatureMaps && this.viewer != null) {
      this.viewer.featureMaps = undefined;
      this.stateMap.shouldClearFeatureMaps = undefined;
    }
  }

  private setCameraAndKeyboardControls(): void {
    if (this.mode === 'edit' && this.viewer != null) {
      // Check camera controls
      if (this.viewer.cameraControls) {
        this.stateMap.shouldClearCameraControls = true;
        this.viewer.cameraControls = false;
      }

      // Check keyboard controls
      if (this.viewer.keyboardControls) {
        this.stateMap.shouldClearKeyboardControls = true;
        this.viewer.keyboardControls = false;
      }
    }
  }

  private resetCameraAndKeyboardControls(): void {
    if (this.viewer != null) {
      if (this.stateMap.shouldClearCameraControls) {
        this.viewer.cameraControls = true;
        this.stateMap.shouldClearCameraControls = undefined;
      }

      if (this.stateMap.shouldClearKeyboardControls) {
        this.viewer.keyboardControls = true;
        this.stateMap.shouldClearKeyboardControls = undefined;
      }
    }
  }
}
